import store from "..";
import { generatePkcePair } from "../../common/pkce";
import {
  jflApi,
  getDeviceId,
  setSessionExpiredHandler,
} from "../../common/authApi";
import { loginDetail } from "./login";
import { clearDeviceDropdownData } from "./deviceDropdownSelection";
import { resetState } from "../actionTypes";
import { fetchStores } from "./stores";

const errorMessage = (error, fallback) =>
  error.response?.data?.error?.errorMessage ||
  error.response?.data?.message ||
  error.message ||
  fallback;

// GET /v1/users/me. Kept under login.profile, which is where the app already
// looks for the signed-in user. Goes through jflApi, so a 401 refreshes and
// retries rather than losing the profile.
export const fetchCurrentUser = async () => {
  try {
    const response = await jflApi.get("/v1/users/me");
    const profile = response.data?.data ?? null;

    store.dispatch(loginDetail({ ...store.getState().login, profile }));

    return { success: true, data: profile };
  } catch (error) {
    // Explicit null so the UI can tell a failed load from one still in flight
    // (undefined), instead of reporting failure while the call is pending.
    store.dispatch(loginDetail({ ...store.getState().login, profile: null }));

    return {
      success: false,
      message: errorMessage(error, "Could not load profile"),
    };
  }
};

// PKCE authorization code flow against the JFL auth service.
//
// 1. generate a verifier/challenge pair
// 2. POST /authorize with the credentials + challenge -> authorization code
// 3. POST /token with the code + verifier -> access/refresh/csrf cookies
//
// Nothing is returned in the token response body; success is the cookies being
// set, which is why step 3 only checks that the call did not throw.
export const loginWithPkce = async (emailAddress, password) => {
  try {
    const { codeVerifier, codeChallenge } = await generatePkcePair();
    const deviceId = getDeviceId();

    const authorize = await jflApi.post("/v1/auth/authorize", {
      emailAddress,
      password,
      deviceId,
      codeChallenge,
    });

    const authorizationCode = authorize.data?.data?.authorizationCode;
    if (!authorizationCode) {
      return {
        success: false,
        message: authorize.data?.message || "Authorization failed",
      };
    }

    // The code is valid for 60 seconds, so this follows immediately.
    await jflApi.post("/v1/auth/token", {
      grantType: "authorization_code",
      authorizationCode,
      codeVerifier,
      deviceId,
    });

    store.dispatch(loginDetail({ emailAddress, deviceId }));

    // Both of these need the session that now exists, and neither failing
    // invalidates the login itself - they report separately so the UI can say
    // what is missing. Run together: concurrent 401s share one refresh.
    const [profile, stores] = await Promise.all([
      fetchCurrentUser(),
      fetchStores(),
    ]);

    return {
      success: true,
      message: "Sign in successful",
      profileLoaded: profile.success,
      profileMessage: profile.message,
      storesLoaded: stores.success,
      storesMessage: stores.message,
    };
  } catch (error) {
    return { success: false, message: errorMessage(error, "Login failed") };
  }
};

// POST /v1/auth/changePassword for the signed in user's own password.
//
// The response carries a fresh access, refresh and csrf cookie, so the session
// would technically keep working. The caller signs the user out anyway, which
// is what the server's own message asks for and means the new password gets
// used at least once.
export const changePassword = async ({ currentPassword, newPassword }) => {
  try {
    const response = await jflApi.post("/v1/auth/changePassword", {
      currentPassword,
      newPassword,
    });

    return { success: true, message: response.data?.message };
  } catch (error) {
    return {
      success: false,
      message: errorMessage(error, "Could not change password"),
    };
  }
};

// Clears everything, not just the login slice: the store holds the selected
// store, rooms and device data of whoever was signed in, and leaving that
// behind would show it to the next person to sign in. Same three dispatches
// the header's logout button makes.
export const logout = () => {
  store.dispatch(loginDetail({}));
  store.dispatch(clearDeviceDropdownData());
  store.dispatch({ type: resetState.RESET });
};

// Called when a refresh fails, i.e. the refresh token is gone or rejected and
// there is no way back to a valid session without signing in again.
setSessionExpiredHandler(() => {
  logout();
});
